import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Order } from '../../Module/Order.model';
import { OrderService } from '../../service/order.service';
import { PopupService } from '../../service/popup.service';
import { ProductService } from '../../service/product.service';
import { InsufficientQuantityDialogComponent } from '../../insufficient-quantity-dialog/insufficient-quantity-dialog.component';
import { GenericErrorDialogComponent } from '../../generic-error-dialog/generic-error-dialog.component';

export interface F8LinkConfirmDialogData {
  order: Order;
  amount: number;
}

@Component({
  selector: 'app-f8-link-confirm-dialog',
  templateUrl: './f8-link-confirm-dialog.component.html',
  styleUrls: ['./f8-link-confirm-dialog.component.css']
})
export class F8LinkConfirmDialogComponent {

  isLoading = false;

  constructor(
    public dialogRef: MatDialogRef<F8LinkConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: F8LinkConfirmDialogData,
    private orderService: OrderService,
    private popupService: PopupService,
    private matDialog: MatDialog,
    private productService: ProductService
  ) {}

  get effectiveTotal(): number {
    const order = this.data.order;
    if (order.netTotal != null && order.netTotal > 0) return order.netTotal;
    if (order.orderLines && order.orderLines.length > 0) {
      return order.calculateTotal() - (order.discount || 0);
    }
    return this.data.amount;
  }

  get clientName(): string {
    const c = this.data.order.clientDTO;
    return c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : '';
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.isLoading) return;
    if (event.key === 'Escape')  { event.preventDefault(); this.onCancel(); }
    if (event.key === 'Enter')   { event.preventDefault(); this.onConfirm(); }
    if (event.key === 'n' || event.key === 'N') { event.preventDefault(); this.onRefuse(); }
  }

  /**
   * Lier — pour les nouvelles commandes : crée la commande puis ferme le dialog avec l'ID.
   * La navigation est gérée par ShortcutService pour éviter tout problème de zone Angular.
   */
  onConfirm(): void {
    if (this.isLoading) return;
    if (!this.data.order.id) {
      this.isLoading = true;
      // Copie sans mutation de l'objet partagé avec AddOrderComponent
      const orderPayload = { ...this.data.order, isPaid: 'true', isVirtual: true };
      this.orderService.addOrder(orderPayload as any).subscribe({
        next: (response: any) => {
          const savedOrder = response.body || response;
          if (!savedOrder?.id) {
            this.isLoading = false;
            this.popupService.showError('Erreur lors de la sauvegarde de la commande', 3000);
            return;
          }
          // Fermer avec l'ID — ShortcutService gère la navigation et le lien F8
          this.dialogRef.close(savedOrder.id as number);
        },
        error: (error: any) => {
          this.isLoading = false;
          const errorMsg: string = error?.error?.message || '';

          if (errorMsg.includes('Stock insuffisant')) {
            const productNameMatch = errorMsg.match(/produit\s*:\s*(.+)/);
            const productIdMatch   = errorMsg.match(/ID du produit\s*:\s*(\d+)/);
            const stockMatch       = errorMsg.match(/Stock disponible\s*:\s*([\d.]+)/);

            const productName = productNameMatch ? productNameMatch[1].trim() : '';
            const productId   = productIdMatch   ? parseInt(productIdMatch[1], 10)  : 0;
            const available   = stockMatch       ? parseFloat(stockMatch[1])        : 0;

            const stockDialog = this.matDialog.open(InsufficientQuantityDialogComponent, {
              data: { productName, available, newQuantity: available },
              disableClose: false,
              width: '450px',
              panelClass: 'transparent-dialog-container'
            });

            stockDialog.afterClosed().subscribe((newQty: number | null) => {
              if (newQty == null) return;
              this.productService.updateProductQuantity(productId, newQty).subscribe({
                next: () => {
                  // Retry the order creation after stock is updated
                  this.onConfirm();
                },
                error: () => {
                  this.matDialog.open(GenericErrorDialogComponent, {
                    data: { message: 'Erreur lors de la mise à jour du stock.' }
                  });
                }
              });
            });
          } else {
            this.matDialog.open(GenericErrorDialogComponent, {
              data: { message: errorMsg || 'Erreur lors de la sauvegarde de la commande.' }
            });
          }
        }
      });
    } else {
      this.dialogRef.close(this.data.order.id as number);
    }
  }

  /** F8 normal (N) */
  onRefuse(): void { this.dialogRef.close(0); }

  /** Annuler tout (Echap) */
  onCancel(): void { this.dialogRef.close(null); }
}
